import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import { useJobMasterEntryScreen } from './hooks/useJobMasterEntryScreen'
import { appToast } from '../../../components/AppToast'
import MasterApprovalBanner from '../../approval/components/MasterApprovalBanner'

function SelectParentField({ value, onChange, options }) {
  return (
    <AppDropdown
      value={value}
      onChange={onChange}
      placeholder="Select a Parent Task Template"
      triggerClassName="w-full rounded-xl border-none bg-surface-container-low px-4 py-3.5 text-sm text-on-surface focus:ring-2 focus:ring-primary/20 transition"
      options={options.map((opt) => ({
        value: String(opt.id),
        label: `${opt.task_type_name} (${opt.task_type_code})`,
      }))}
    />
  )
}

export default function JobMasterEntryScreen() {
  const { state, actions } = useJobMasterEntryScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  const selectedParentTask = useMemo(
    () =>
      state.taskMasterOptions.find(
        (opt) => String(opt.id) === String(state.form.parent_task_master_id),
      ) || null,
    [state.form.parent_task_master_id, state.taskMasterOptions],
  )

  const sequenceSteps = [0, 1, 2, 3, 4, 5, 6, 7, 8]
  const currentSeq = Number(state.form.sequence_order)

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-8 pb-10">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-secondary-container px-3 py-1">
            <span className="material-symbols-outlined text-sm text-primary">checklist_rtl</span>
            <span className="text-[10px] font-black uppercase tracking-[0.18em] text-on-secondary-container">
              Checklist Sub-Task
            </span>
          </div>
          <h1 className="text-4xl font-extrabold leading-tight tracking-tight text-on-surface">
            {state.isEditMode ? 'Edit Job Blueprint' : 'New Job Blueprint'}
          </h1>
          <p className="mt-2 text-sm text-on-surface-variant">
            Define a modular sub-task step that auto-populates under a parent Task blueprint.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {!state.pendingApproval && (
            <Link
              to="/masters/job-master/list"
              className={`rounded-lg bg-surface-container-high px-6 py-2.5 text-sm font-bold text-on-surface-variant transition hover:bg-surface-container-highest ${
                state.isSaving ? 'pointer-events-none opacity-50' : ''
              }`}
            >
              Discard
            </Link>
          )}
          <button
            type="button"
            onClick={actions.handleSave}
            disabled={state.isSaving || state.isLoading || Boolean(state.pendingApproval)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-2.5 text-sm font-bold text-on-primary shadow-md shadow-primary/25 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-base">
              {state.pendingApproval ? 'schedule' : 'send'}
            </span>
            {state.pendingApproval ? 'Submitted for Approval' : state.isSaving ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      </header>

      <MasterApprovalBanner pendingApproval={state.pendingApproval} queueRoute="/approval/masters/list?module=job_master" />

      {state.isLoading ? (
        <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest px-6 py-16 text-center text-sm text-on-surface-variant">
          Loading job blueprint...
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1fr_340px]">
          {/* ── Left: Form ─────────────────────────────────── */}
          <div className="space-y-6">
            {/* ── Section 1: Job Identity ──────────────────── */}
            <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-7">
              <div className="mb-5 flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">label</span>
                <h2 className="text-base font-black uppercase tracking-widest text-on-surface-variant">Job Identity</h2>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Job Type Name <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={state.form.job_type_name}
                    onChange={(e) => actions.patchForm('job_type_name', e.target.value)}
                    placeholder="e.g., Database Schema Update"
                    className="mt-2 w-full rounded-xl border-none bg-surface-container-low px-5 py-4 text-xl font-bold text-on-surface outline-none placeholder:text-on-surface-variant/50 transition focus:ring-2 focus:ring-primary/20"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Parent Task Blueprint <span className="text-error">*</span>
                  </label>
                  <div className="mt-2">
                    <SelectParentField
                      value={state.form.parent_task_master_id}
                      onChange={(value) => actions.patchForm('parent_task_master_id', value)}
                      options={state.taskMasterOptions}
                    />
                  </div>
                  {selectedParentTask && (
                    <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary">
                      <span className="material-symbols-outlined text-xs">check_circle</span>
                      Linked to: {selectedParentTask.task_type_name}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* ── Section 2: THE HERO — Checklist Sequence ─── */}
            <section className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-primary-container/10 to-surface-container-lowest p-7">
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
                    format_list_numbered
                  </span>
                  <h2 className="text-base font-black uppercase tracking-widest text-on-surface-variant">Checklist Sequence</h2>
                </div>
                <div className="rounded-full bg-primary px-4 py-1 text-sm font-black text-on-primary">
                  Step #{currentSeq}
                </div>
              </div>

              {/* Step picker */}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => actions.patchForm('sequence_order', String(Math.max(0, currentSeq - 1)))}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-surface-container-low text-on-surface shadow-sm transition hover:bg-surface-container-high"
                >
                  <span className="material-symbols-outlined">remove</span>
                </button>

                <div className="flex flex-1 gap-1.5 overflow-x-auto py-1">
                  {sequenceSteps.map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => actions.patchForm('sequence_order', String(num))}
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-black transition-all ${currentSeq === num
                        ? 'bg-primary text-on-primary shadow-md scale-110'
                        : 'bg-surface-container-low text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high'
                        }`}
                    >
                      #{num}
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => actions.patchForm('sequence_order', String(currentSeq + 1))}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-surface-container-low text-on-surface shadow-sm transition hover:bg-surface-container-high"
                >
                  <span className="material-symbols-outlined">add</span>
                </button>
              </div>

              {/* Timeline placement visualization */}
              <div className="mt-6 rounded-xl bg-surface-container-low/60 p-4">
                <p className="mb-3 text-[10px] font-black uppercase tracking-wider text-on-surface-variant">
                  Checklist Timeline Placement
                </p>
                <div className="flex items-center gap-2 overflow-x-auto text-xs flex-wrap">
                  <span className="shrink-0 rounded-lg border border-outline-variant/15 bg-surface-container-lowest px-3 py-1.5 font-bold text-on-surface-variant opacity-50">
                    Start
                  </span>
                  <span className="text-outline-variant">▸</span>

                  {currentSeq > 0 && (
                    <>
                      <span className="shrink-0 rounded-lg border border-outline-variant/15 bg-surface-container-lowest px-3 py-1.5 font-bold text-on-surface-variant opacity-40">
                        Step #{currentSeq - 1}
                      </span>
                      <span className="text-outline-variant">▸</span>
                    </>
                  )}

                  <span className="shrink-0 rounded-lg bg-primary px-4 py-1.5 font-black text-on-primary shadow-sm">
                    {state.form.job_type_name.trim() || 'This Sub-Task'} (Step #{currentSeq})
                  </span>

                  <span className="text-outline-variant">▸</span>
                  <span className="shrink-0 rounded-lg border border-outline-variant/15 bg-surface-container-lowest px-3 py-1.5 font-bold text-on-surface-variant opacity-40">
                    Step #{currentSeq + 1}
                  </span>
                </div>
              </div>
            </section>

            {/* ── Section 3: Effort & Status ───────────────── */}
            <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-7">
              <div className="mb-5 flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">speed</span>
                <h2 className="text-base font-black uppercase tracking-widest text-on-surface-variant">Effort & Status</h2>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="grid gap-2">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                    Default Effort Estimate <span className="text-error">*</span>
                  </span>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="grid gap-1">
                      <span className="text-[10px] font-semibold text-outline uppercase tracking-wider">Hours</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={state.form.effort_hours}
                        onChange={(e) => actions.patchForm('effort_hours', e.target.value)}
                        placeholder="e.g. 1"
                        className="w-full rounded-xl border-none bg-surface-container-low py-3 px-4 text-sm text-on-surface outline-none"
                      />
                    </label>
                    <label className="grid gap-1">
                      <span className="text-[10px] font-semibold text-outline uppercase tracking-wider">Minutes</span>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        step="1"
                        value={state.form.effort_minutes}
                        onChange={(e) => actions.patchForm('effort_minutes', e.target.value)}
                        placeholder="e.g. 30"
                        className="w-full rounded-xl border-none bg-surface-container-low py-3 px-4 text-sm text-on-surface outline-none"
                      />
                    </label>
                  </div>
                </div>

                <div className="grid gap-2">
                  <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Operational Status</span>
                  <div className="flex gap-2 rounded-xl bg-surface-container-low p-1">
                    <button
                      type="button"
                      onClick={() => actions.setOperationalStatus('active')}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-bold transition-all ${state.operationalStatus === 'active'
                        ? 'bg-surface-container-lowest text-primary shadow-sm'
                        : 'text-on-surface-variant'
                        }`}
                    >
                      <span className="h-2 w-2 rounded-full bg-emerald-500" />
                      Active
                    </button>
                    <button
                      type="button"
                      onClick={() => actions.setOperationalStatus('inactive')}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-bold transition-all ${state.operationalStatus === 'inactive'
                        ? 'bg-surface-container-lowest text-on-surface shadow-sm'
                        : 'text-on-surface-variant'
                        }`}
                    >
                      <span className="h-2 w-2 rounded-full bg-outline" />
                      Inactive
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* ── Section 4: SLA Override ──────────────────── */}
            <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-7">
              <div className="mb-5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">hourglass_empty</span>
                  <h2 className="text-base font-black uppercase tracking-widest text-on-surface-variant">SLA Override</h2>
                </div>
                {state.form.sla_hours && (
                  <span className="rounded-full bg-amber-100 px-3 py-0.5 text-xs font-black text-amber-700">
                    Override Active
                  </span>
                )}
              </div>

              <label className="flex cursor-pointer items-center gap-4 rounded-xl border border-outline-variant/20 bg-surface-container-low p-4 transition hover:bg-surface-container-high/50">
                <input
                  type="checkbox"
                  checked={Boolean(state.form.sla_hours)}
                  onChange={(e) => actions.patchForm('sla_hours', e.target.checked ? '24' : '')}
                  className="h-4 w-4 rounded border-outline-variant accent-primary"
                />
                <div>
                  <p className="text-sm font-bold text-on-surface">Enable Custom SLA Override</p>
                  <p className="text-xs text-on-surface-variant">
                    Override the parent Task Blueprint's default deadline for this step only.
                  </p>
                </div>
              </label>

              {Boolean(state.form.sla_hours) ? (
                <div className="mt-4">
                  <label className="grid gap-2">
                    <span className="text-[11px] font-black uppercase tracking-[0.14em] text-on-surface-variant">
                      Override SLA Target <span className="text-error">*</span>
                    </span>
                    <div className="relative">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={state.form.sla_hours}
                        onChange={(e) => actions.patchForm('sla_hours', e.target.value)}
                        placeholder="24"
                        className="w-full rounded-xl border border-amber-300/50 bg-amber-50 py-3 pl-4 pr-16 text-sm text-on-surface outline-none focus:ring-2 focus:ring-amber-400/40"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-bold text-amber-700">hours</span>
                    </div>
                    <p className="text-[10px] font-semibold text-amber-700">
                      ⚠ This {state.form.sla_hours}-hour SLA will override the parent Blueprint's default.
                    </p>
                  </label>
                </div>
              ) : (
                <div className="mt-4 flex items-start gap-2 rounded-xl border border-primary/15 bg-primary/5 p-4 text-xs font-semibold text-primary">
                  <span className="material-symbols-outlined text-sm">info</span>
                  <span>Inheriting the parent Task Blueprint's SLA timeline.</span>
                </div>
              )}
            </section>
          </div>

          {/* ── Right: Live preview + info ──────────────── */}
          <aside className="space-y-5">
            <div className="sticky top-6 space-y-5">

              {/* Blueprint context */}
              <div className="rounded-2xl bg-surface-container-low p-5 space-y-3">
                <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary">About Job Blueprints</h3>
                <div className="space-y-2.5 text-xs text-on-surface-variant">
                  <p className="flex items-start gap-2">
                    <span className="material-symbols-outlined mt-0.5 text-sm text-primary">info</span>
                    Parent templates aggregate child checklist workloads automatically.
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="material-symbols-outlined mt-0.5 text-sm text-primary">warning</span>
                    SLA overrides take precedence over parent Task deadlines.
                  </p>
                  <p className="flex items-start gap-2">
                    <span className="material-symbols-outlined mt-0.5 text-sm text-primary">swap_vert</span>
                    Sequence order establishes the checklist priority on ticket handlers.
                  </p>
                </div>
              </div>

              {/* Completion */}
              <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-[0.14em] text-on-surface-variant">Completion</span>
                  <span className={`text-lg font-black ${state.completionPercent >= 80 ? 'text-emerald-600' : state.completionPercent >= 50 ? 'text-amber-600' : 'text-error'}`}>
                    {state.completionPercent}%
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-surface-variant">
                  <div
                    className={`h-full rounded-full transition-all ${state.completionPercent >= 80 ? 'bg-emerald-500' : state.completionPercent >= 50 ? 'bg-amber-500' : 'bg-error'}`}
                    style={{ width: `${Math.max(4, state.completionPercent)}%` }}
                  />
                </div>
              </div>

            </div>
          </aside>
        </div>
      )}
    </div>
  )
}
